import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";import { useAuth } from "../lib/auth";
import { Pagination, usePagination } from "../components/Pagination";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import Barcode from "../components/Barcode";
import BarcodeScanner from "../components/BarcodeScanner";
import { toast } from "sonner";
import { Plus, Printer, Camera, MagnifyingGlass, PencilSimple, Trash, Package, X, CheckSquare, Funnel, SortAscending, Warning } from "@phosphor-icons/react";
import { printDom } from "../lib/printDom";
import { useRequestDelete } from "./Requests";
import useHIDScanner from "../lib/useHIDScanner";

export default function Inventory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [summary, setSummary] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addRmOpen, setAddRmOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [filter, setFilter] = useState("pool_pending");
  const [rmFilter, setRmFilter] = useState(null);   // {id, name} — filter table to just this RM
  const [sort, setSort] = useState("received_desc");
  const [search, setSearch] = useState("");
  const [print, setPrint] = useState(null);
  const [editCost, setEditCost] = useState(null);
  const [markUsed, setMarkUsed] = useState(null);
  const [bagPool, setBagPool] = useState(null);
  const [historyBag, setHistoryBag] = useState(null);
  const [usageBag, setUsageBag] = useState(null);   // Bag whose consumption history should be shown
  const [bulkPrintOpen, setBulkPrintOpen] = useState(false);
  const [stockTab, setStockTab] = useState("rm");   // "rm" | "fg" — hides RM bag table when FG is active
  // Bags sitting partially open longer than the threshold (default 7 days).
  const [leftoverAlerts, setLeftoverAlerts] = useState({ count: 0, bags: [], threshold_days: 7 });
  const [showAlerts, setShowAlerts] = useState(false);
  const requestDelete = useRequestDelete();

  const load = useCallback(async () => {
    // Map UI filter → server params. "pool_pending" = PO pool bags still
    // in_stock with weight remaining, "in_stock" = physical bags only.
    let invParams = {};
    if (filter === "pool_pending") invParams = { status: "in_stock", is_pool: true };
    else if (filter === "in_stock") invParams = { status: "in_stock", is_pool: false };
    else if (filter !== "all") invParams = { status: filter };
    // When an RM tile is selected, layer the raw_material_id filter on TOP of
    // whatever status/is_pool filter the operator has selected — so the RM
    // filter works in every view ("Consumed", "Bagged Out", "All", etc.),
    // not just in "In-Stock Physical Bags".
    if (rmFilter) {
      invParams = { ...invParams, raw_material_id: rmFilter.id };
    }
    const [a, b, c, d] = await Promise.all([
      api.get("/inventory", { params: invParams }),
      api.get("/raw-materials"),
      api.get("/inventory/summary"),
      api.get("/inventory/partial-alerts").catch(() => ({ data: { count: 0, bags: [], threshold_days: 7 } })),
    ]);
    setItems(a.data); setMaterials(b.data); setSummary(c.data); setLeftoverAlerts(d.data);
  }, [filter, rmFilter]);
  useEffect(() => { load(); }, [load]);

  useHIDScanner({
    onScan: async (code) => {
      try {
        const r = await api.get(`/inventory/by-barcode/${encodeURIComponent(code)}`);
        setSearch(code);
        toast.success(`Scanned: ${r.data.raw_material_name} · ${r.data.remaining_weight}${r.data.unit}`);
      } catch (e) {
        toast.error(`Unknown barcode: ${code}`);
      }
    },
    enabled: true,
  });

  const filtered = items.filter(i =>
    !search || (i.barcode || "").toLowerCase().includes(search.toLowerCase())
    || i.raw_material_name.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    const cmp = {
      received_desc: (a, b) => (b.received_at || "").localeCompare(a.received_at || ""),
      received_asc: (a, b) => (a.received_at || "").localeCompare(b.received_at || ""),
      weight_desc: (a, b) => (b.remaining_weight || 0) - (a.remaining_weight || 0),
      weight_asc: (a, b) => (a.remaining_weight || 0) - (b.remaining_weight || 0),
      material_asc: (a, b) => (a.raw_material_name || "").localeCompare(b.raw_material_name || ""),
    }[sort] || cmp?.received_desc;
    arr.sort(cmp);
    return arr;
  }, [filtered, sort]);

  const { page, setPage, pageSize, setPageSize } = usePagination(20);
  useEffect(() => { setPage(1); }, [search, filter, sort, setPage]);
  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);

  const selectableIds = React.useMemo(
    () => paged.filter(i => i.status === "in_stock").map(i => i.id),
    [paged],
  );
  const allSelected = selectableIds.length > 0
    && selectableIds.every(id => selectedIds.has(id));
  const toggleOne = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const togglePage = () => setSelectedIds(prev => {
    const next = new Set(prev);
    if (allSelected) { selectableIds.forEach(id => next.delete(id)); }
    else { selectableIds.forEach(id => next.add(id)); }
    return next;
  });
  const clearSel = () => setSelectedIds(new Set());

  const bulkMarkUsed = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const reason = window.prompt(
      `Mark ${ids.length} bag(s) as used. Optional reason (visible in audit log):`,
      "",
    );
    if (reason === null) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await api.post(`/inventory/${id}/mark-used`, { reason });
        ok += 1;
      } catch { fail += 1; }
    }
    setBulkBusy(false);
    clearSel();
    load();
    if (fail === 0) toast.success(`${ok} bag(s) marked used`);
    else toast.error(`Marked ${ok} · ${fail} failed`);
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto space-y-6">
      <PageHeader
        label="WAREHOUSE"
        title="Raw Material Inventory"
        subtitle="FIFO-managed stock. Each bag gets a Code128 barcode."
        action={
          <div className="flex gap-2 flex-wrap items-center">
            <Btn variant="secondary" onClick={() => setAddRmOpen(true)} data-testid="add-rm-btn" className="flex items-center gap-1.5 border-zinc-200 shadow-xs hover:bg-zinc-50 transition-all">
              <Plus size={14} className="text-zinc-600" /> Raw Material
            </Btn>
            <Btn variant="secondary" onClick={() => setScanOpen(true)} data-testid="scan-btn" className="flex items-center gap-1.5 border-zinc-200 shadow-xs hover:bg-zinc-50 transition-all">
              <Camera size={14} className="text-zinc-600" /> Scan Label
            </Btn>
            <Btn variant="secondary" onClick={() => setBulkPrintOpen(true)} data-testid="bulk-print-btn" className="flex items-center gap-1.5 border-zinc-200 shadow-xs hover:bg-zinc-50 transition-all">
              <Printer size={14} className="text-zinc-600" /> Bulk Audit
            </Btn>
            <Btn onClick={() => setAddOpen(true)} data-testid="add-bag-btn" className="flex items-center gap-1.5 bg-zinc-900 text-white hover:bg-orange-600 shadow-sm transition-all">
              <Plus size={14} /> Add Bag
            </Btn>
          </div>
        }
      />

      {/* --- Stock overview: unified card with tabs (RM silos / Finished goods).
          Click an RM silo to filter the bag table below to that material. */}
      <StockOverview
        materials={materials}
        activeRmId={rmFilter?.id || null}
        onTabChange={setStockTab}
        onPickRm={(sel) => {
          setRmFilter(sel);
          // Scroll to bag table so the operator sees the filtered result.
          if (sel) setTimeout(() => {
            document.querySelector('[data-testid="inv-bag-table"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        }}
      />

      {/* --- Everything below is RM-focused; hide it when the operator is on the Finished Goods tab.
          Keeps the FG list front-and-center without the bag-level noise. */}
      {stockTab === "rm" && (<>

      {/* Leftover bag alerts — bags sitting partially open > 7 days */}
      {leftoverAlerts.count > 0 && (
        <div
          className="border border-amber-300 bg-amber-50 rounded-lg p-3 flex items-start gap-3"
          data-testid="leftover-alerts-banner"
        >
          <Warning size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-amber-900">
              {leftoverAlerts.count} bag{leftoverAlerts.count > 1 ? "s" : ""} sitting partially open for {leftoverAlerts.threshold_days ?? 7}+ days
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              These bags were opened for an earlier batch and still have leftover stock. Consume or write them off before they get forgotten on the floor.
            </div>
            {showAlerts && (
              <div className="mt-2 space-y-1" data-testid="leftover-alerts-list">
                {leftoverAlerts.bags.slice(0, 20).map(b => {
                  const lastNote = (b.partial_history || []).slice(-1)[0]?.note;
                  return (
                    <div
                      key={b.id}
                      className="text-[11px] font-mono bg-white border border-amber-200 rounded px-2 py-1 flex flex-wrap items-center gap-2"
                      data-testid={`leftover-alert-${b.barcode}`}
                    >
                      <button
                        type="button"
                        onClick={() => setSearch(b.barcode)}
                        className="font-bold text-amber-900 hover:underline"
                      >
                        {b.barcode}
                      </button>
                      <span className="text-zinc-700">{b.raw_material_name}</span>
                      <span className="text-zinc-500">· {b.remaining_weight}{b.unit} left</span>
                      <button
                        type="button"
                        onClick={() => {
                          const full = items.find(x => x.id === b.id) || b;
                          setHistoryBag(full);
                        }}
                        className="text-amber-800 hover:text-amber-900 underline text-[10px] uppercase tracking-wider"
                        data-testid={`leftover-alert-history-${b.barcode}`}
                      >
                        View history
                      </button>
                      <span className="ml-auto text-amber-700">open {b.age_days}d</span>
                      {lastNote && <span className="w-full text-[10px] text-zinc-600 italic">note: {lastNote}</span>}
                    </div>
                  );
                })}
                {leftoverAlerts.bags.length > 20 && (
                  <div className="text-[11px] text-zinc-500 italic">+ {leftoverAlerts.bags.length - 20} more…</div>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowAlerts(v => !v)}
            className="text-xs font-mono uppercase tracking-widest text-amber-800 hover:text-amber-900 underline"
            data-testid="leftover-alerts-toggle"
          >
            {showAlerts ? "Hide" : "View"}
          </button>
        </div>
      )}


      {/* Filtered-by-RM chip — shown when a silo tile is selected above */}
      {rmFilter && (
        <div className="flex items-center gap-2 bg-orange-50 border border-orange-300 rounded-lg px-3 py-2 text-sm" data-testid="rm-filter-chip">
          <span className="text-[10px] font-mono uppercase tracking-widest text-orange-800">Filtered by RM</span>
          <span className="font-bold text-zinc-900">{rmFilter.name}</span>
          {rmFilter.code && <span className="text-xs font-mono text-zinc-500">· {rmFilter.code}</span>}
          <button
            type="button"
            onClick={() => setRmFilter(null)}
            className="ml-auto text-xs font-mono uppercase tracking-widest text-orange-700 hover:text-orange-900"
            data-testid="rm-filter-clear"
          >
            Clear ✕
          </button>
        </div>
      )}

      {/* Main Stock Data Table Core Component Layout */}
      <Card className="!p-0 border border-zinc-200/80 shadow-sm overflow-hidden bg-white rounded-xl" data-testid="inv-bag-table">
        {/* Dynamic Multi Selection Floating Panel Ribbon */}
        {isAdmin && selectedIds.size > 0 && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-inner animate-slide-down"
            data-testid="inv-bulk-bar"
          >
            <div className="flex items-center gap-3">
              <span className="p-1 rounded-md bg-white/20">
                <CheckSquare size={16} weight="fill" />
              </span>
              <span className="text-xs font-mono uppercase tracking-wider font-bold">
                {selectedIds.size} matching items selected
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={clearSel}
                className="text-xs font-mono uppercase tracking-wider hover:underline text-white/90 font-medium"
                data-testid="inv-bulk-clear"
              >
                Clear Selection
              </button>
              <Btn
                onClick={bulkMarkUsed}
                disabled={bulkBusy}
                data-testid="inv-bulk-mark-used"
                className="!bg-white !text-zinc-950 hover:!bg-zinc-50 border-transparent rounded-lg shadow-sm font-bold text-xs"
              >
                {bulkBusy ? "Processing Requests…" : `Mark Bulk Consumed`}
              </Btn>
            </div>
          </div>
        )}

        {/* Filters and Utilities Top Workspace Ribbon */}
        <div className="flex flex-wrap items-center gap-3 p-4 bg-zinc-50/50 border-b border-zinc-200/80">
          <div className="relative flex-1 min-w-[260px]">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search barcodes or dynamic material entries..."
              data-testid="inv-search"
              className="w-full pl-9 pr-3 py-1.5 border border-zinc-300 rounded-lg text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 shadow-2xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Funnel size={14} className="text-zinc-400 shrink-0" />
            <Select value={filter} onChange={e => setFilter(e.target.value)} data-testid="inv-filter" className="!w-auto !text-xs !py-1.5 rounded-lg shadow-2xs border-zinc-200">
              <option value="pool_pending">Pending Pool Bags (default)</option>
              <option value="in_stock">In-Stock Physical Bags</option>
              <option value="consumed">Consumed</option>
              <option value="bagged_out">Bagged Out</option>
              <option value="all">All Items</option>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <SortAscending size={14} className="text-zinc-400 shrink-0" />
            <Select value={sort} onChange={e => setSort(e.target.value)} data-testid="inv-sort" className="!w-auto !text-xs !py-1.5 rounded-lg shadow-2xs border-zinc-200">
              <option value="received_desc">Received: newest first</option>
              <option value="received_asc">Received: oldest first</option>
              <option value="weight_desc">Weight: high → low</option>
              <option value="weight_asc">Weight: low → high</option>
              <option value="material_asc">Material A → Z</option>
            </Select>
          </div>
        </div>

        {/* Content Data Table Presentation Frame */}
        {sorted.length === 0 ? (
          <div className="p-12 text-center bg-white"><EmptyState title="No stock items discovered" hint="Generate custom items or add full inventory bags to populate this index view." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="kr-table w-full text-left border-collapse">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 font-mono text-[11px] uppercase tracking-wider">
                  {isAdmin && (
                    <th className="w-10 px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={togglePage}
                        disabled={selectableIds.length === 0}
                        className="w-4 h-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500 accent-orange-600 cursor-pointer"
                        data-testid="inv-select-all"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3">Barcode Serial</th>
                  <th className="px-4 py-3">Material Descriptor</th>
                  <th className="px-4 py-3">Receipt Stamp</th>
                  <th className="px-4 py-3">Original Volume</th>
                  <th className="px-4 py-3">Current Remaining</th>
                  <th className="px-4 py-3">Unit Valuation</th>
                  <th className="px-4 py-3">Status Block</th>
                  <th className="px-4 py-3 text-right">Row Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 text-sm">
                {paged.map(i => {
                  const isPool = i.status === "pending_bagging" || i.is_pool === true;
                  return (
                    <tr
                      key={i.id}
                      data-testid={`inv-row-${i.barcode || i.id}`}
                      className={`hover:bg-zinc-50/50 transition-colors group ${isPool ? "bg-amber-50/20" : ""}`}
                    >
                      {isAdmin && (
                        <td className="px-4 py-3.5 text-center">
                          {i.status === "in_stock" ? (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(i.id)}
                              onChange={() => toggleOne(i.id)}
                              data-testid={`inv-select-${i.barcode}`}
                              className="w-4 h-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500 accent-orange-600 cursor-pointer"
                            />
                          ) : <div className="w-4 h-4 mx-auto opacity-20 text-zinc-300">—</div>}
                        </td>
                      )}
                      <td className="px-4 py-3.5 font-mono text-orange-600 font-extrabold tracking-tight">
                        {i.barcode || <span className="text-amber-600/70 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded text-xs font-normal italic">PO Pool System</span>}
                        {i.partial_since && i.status === "in_stock" && (() => {
                          const ageDays = Math.floor((Date.now() - new Date(i.partial_since).getTime()) / 86400000);
                          const lastNote = (i.partial_history || []).slice(-1)[0]?.note;
                          const stale = ageDays >= (leftoverAlerts.threshold_days ?? 7);
                          return (
                            <span
                              className={`ml-2 inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border cursor-pointer ${stale ? "bg-amber-100 border-amber-300 text-amber-900" : "bg-orange-50 border-orange-200 text-orange-700"}`}
                              title={`Bag opened ${ageDays} day(s) ago${lastNote ? ` · last note: ${lastNote}` : ""} — click for full history`}
                              onClick={(e) => { e.stopPropagation(); setHistoryBag(i); }}
                              data-testid={`inv-partial-${i.barcode}`}
                            >
                              <Warning size={10} weight="fill" />
                              {ageDays}d open
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3.5 font-semibold text-zinc-900">
                        <div>{i.raw_material_name}</div>
                        {isPool && <div className="text-[10px] font-mono text-amber-700 mt-0.5">Linked Pipeline: PO #{i.linked_po_no}</div>}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-xs text-zinc-500">
                        {new Date(i.received_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-zinc-600">{i.weight} <span className="text-[11px] opacity-70">{i.unit}</span></td>
                      <td className="px-4 py-3.5 font-mono font-extrabold text-zinc-900">{i.remaining_weight} <span className="text-[11px] opacity-70">{i.unit}</span></td>
                      <td className="px-4 py-3.5 font-mono">
                        {i.unit_cost > 0
                          ? <span className="text-zinc-900 font-medium">₹{i.unit_cost}</span>
                          : <span className="text-red-500 text-xs bg-red-50 border border-red-100 rounded px-1.5 py-0.5 font-medium inline-block">Cost Missing</span>}
                      </td>
                      <td className="px-4 py-3.5">
                        {(i.status === "bagged_out" || i.status === "consumed") ? (
                          <button
                            type="button"
                            onClick={() => setUsageBag(i)}
                            className="inline-flex items-center gap-1 hover:brightness-110 active:scale-95 transition"
                            data-testid={`inv-status-usage-${i.barcode || i.id}`}
                            title="Click to see where and when this bag was used"
                          >
                            <Badge tone="zinc" className="capitalize !py-0.5 !px-2 shadow-2xs font-medium rounded-md cursor-pointer">
                              {i.status.replace("_", " ")} ↗
                            </Badge>
                          </button>
                        ) : (
                          <Badge tone={i.status === "in_stock" ? "green" : isPool ? "orange" : "zinc"} className="capitalize !py-0.5 !px-2 shadow-2xs font-medium rounded-md">
                            {i.status.replace("_", " ")}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <div className="flex gap-1.5 justify-end items-center opacity-80 sm:opacity-20 group-hover:opacity-100 transition-opacity">
                          {isPool ? (
                            <button
                              data-testid={`gen-bags-${i.id}`}
                              onClick={() => setBagPool(i)}
                              className="text-orange-600 hover:text-white hover:bg-orange-600 border border-orange-200 hover:border-orange-600 px-2.5 py-1 rounded-md font-bold text-[11px] uppercase tracking-wider flex items-center gap-1 transition-all"
                              title="Generate bags with custom weights"
                            >
                              <Package size={13} weight="bold" /> Process Bagging
                            </button>
                          ) : (
                            <>
                              <button
                                data-testid={`edit-cost-${i.barcode}`}
                                onClick={() => setEditCost(i)}
                                className={`p-1.5 rounded-md transition-all ${i.unit_cost > 0 ? "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100" : "text-red-600 bg-red-50 hover:bg-red-100 px-2 py-1 font-bold text-[10px] uppercase tracking-wide border border-red-200"}`}
                                title="Edit Ledger Cost"
                              >
                                {i.unit_cost > 0 ? <PencilSimple size={14} weight="bold" /> : "Set Cost"}
                              </button>
                              <button
                                data-testid={`print-${i.barcode}`}
                                onClick={() => setPrint(i)}
                                className="p-1.5 text-zinc-400 hover:text-orange-600 hover:bg-orange-50 rounded-md transition-all"
                                title="Print Tag Label"
                              >
                                <Printer size={15} weight="bold" />
                              </button>
                              {i.status === "in_stock" && isAdmin && (
                                <button
                                  data-testid={`mark-used-${i.barcode}`}
                                  onClick={() => setMarkUsed(i)}
                                  className="p-1.5 text-zinc-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-all"
                                  title="Mark Manual Ad-Hoc Consumed"
                                >
                                  <CheckSquare size={15} weight="bold" />
                                </button>
                              )}
                            </>
                          )}
                          <button
                            data-testid={`del-inv-${i.barcode || i.id}`}
                            onClick={() => requestDelete({
                              kind: "delete_inventory",
                              id: i.id,
                              label: isPool
                                ? `Pool ${i.raw_material_name} · ${i.remaining_weight}${i.unit} (PO ${i.linked_po_no})`
                                : `Bag ${i.barcode} · ${i.raw_material_name} · ${i.weight}${i.unit}`,
                              directDeleteUrl: `/inventory/${i.id}`,
                              onDone: load,
                            })}
                            className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                            title="Purge Stock Item Record"
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-zinc-100 p-4">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={sorted.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testId="inv-pager"
          />
        </div>
      </Card>
      </>)}

      {/* Modals Configuration Wrappers */}
      {addOpen && <AddBagModal materials={materials} onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); load(); }} />}
      {addRmOpen && <AddRMModal onClose={() => setAddRmOpen(false)} onAdded={() => { setAddRmOpen(false); load(); }} />}
      {bagPool && <GenerateBagsModal pool={bagPool} onClose={() => setBagPool(null)} onDone={() => { setBagPool(null); load(); }} />}
      {scanOpen && (
        <BarcodeScanner
          onClose={() => setScanOpen(false)}
          onDetected={async (code) => {
            setScanOpen(false);
            try {
              const r = await api.get(`/inventory/by-barcode/${code}`);
              toast.success(`${r.data.raw_material_name} · ${r.data.remaining_weight}${r.data.unit} remaining`);
              setSearch(code);
            } catch {
              toast.error("Barcode not found in inventory Verification profiles");
            }
          }}
        />
      )}
      {print && (
        <Modal open onClose={() => setPrint(null)} title="Print Barcode Label Plate">
          <div className="p-2 space-y-5">
            <div data-testid="doc-view" className="border-2 border-dashed border-zinc-300 rounded-xl p-8 text-center bg-zinc-50/50 shadow-inner max-w-sm mx-auto">
              <div className="text-xs font-bold font-mono uppercase tracking-widest text-zinc-400 mb-1">Earth&apos;s Warehouse Profile</div>
              <div className="text-sm font-extrabold text-zinc-900 mb-4">{print.raw_material_name}</div>
              <div className="bg-white p-4 rounded-lg inline-block border border-zinc-200/60 shadow-2xs">
                <Barcode value={print.barcode} height={60} />
              </div>
              <div className="font-mono text-[11px] mt-4 text-zinc-500 font-medium">
                Volume Weight: {print.weight} {print.unit} <br />
                Registered: {new Date(print.received_at).toLocaleDateString("en-IN")}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100 print:hidden">
              <Btn type="button" variant="secondary" onClick={() => setPrint(null)} className="rounded-lg">Dismiss</Btn>
              <Btn onClick={() => printDom('[data-testid="doc-view"]', `Barcode ${print.barcode}`)} data-testid="do-print" className="bg-zinc-950 text-white hover:bg-orange-600 rounded-lg shadow-xs">Trigger Print Output</Btn>
            </div>
          </div>
        </Modal>
      )}
      {editCost && <EditCostModal item={editCost} onClose={() => setEditCost(null)} onSaved={() => { setEditCost(null); load(); }} />}
      {markUsed && <MarkUsedModal item={markUsed} onClose={() => setMarkUsed(null)} onSaved={() => { setMarkUsed(null); load(); }} />}
      {historyBag && <BagHistoryModal bag={historyBag} onClose={() => setHistoryBag(null)} />}
      {usageBag && <BagUsageModal bag={usageBag} onClose={() => setUsageBag(null)} />}
      {bulkPrintOpen && <BulkBagAuditModal onClose={() => setBulkPrintOpen(false)} />}
    </div>
  );
}

// ---------------------------------------------------------- Stock overview
// Unified card with tabs: Raw Materials (silo visualization) + Finished Goods
// (drill-down row list). Clicking a Raw Material silo scrolls to and filters
// the bag table below to show ONLY that RM's in-stock physical bags.
function StockOverview({ materials = [], onPickRm, activeRmId = null, onTabChange }) {
  const [data, setData] = React.useState(null);
  const [tab, setTab] = React.useState("rm");   // "rm" | "fg"
  const [expandedSku, setExpandedSku] = React.useState(null);
  const [q, setQ] = React.useState("");

  const pickTab = (k) => {
    setTab(k);
    if (onTabChange) onTabChange(k);
  };

  React.useEffect(() => {
    api.get("/inventory/stock-summary")
      .then(r => setData(r.data))
      .catch(() => setData({ raw_materials: [], finished_goods: [] }));
  }, []);

  const rms = React.useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return s ? data.raw_materials.filter(r =>
      (r.name || "").toLowerCase().includes(s) || (r.code || "").toLowerCase().includes(s)
    ) : data.raw_materials;
  }, [data, q]);
  const fgs = React.useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return s ? data.finished_goods.filter(f =>
      (f.name || "").toLowerCase().includes(s) || (f.sku_code || "").toLowerCase().includes(s)
    ) : data.finished_goods;
  }, [data, q]);

  const localMax = React.useMemo(
    () => Math.max(1, ...rms.map(r => r.total_weight || 0)),
    [rms]
  );

  if (!data) return <Card className="p-6 text-xs text-zinc-500">Loading stock…</Card>;

  return (
    <Card className="!p-0 overflow-hidden" data-testid="stock-overview">
      {/* Header: tabs + search */}
      <div className="border-b border-zinc-200 px-4 py-3 flex flex-wrap items-center gap-3 justify-between bg-white">
        <div className="flex items-center gap-1">
          <h2 className="font-display text-lg font-black uppercase tracking-tight mr-3">Stock overview</h2>
          {[
            { k: "rm", label: `Raw Materials · ${rms.length}` },
            { k: "fg", label: `Finished Goods · ${fgs.length}` },
          ].map(t => (
            <button
              key={t.k}
              type="button"
              onClick={() => pickTab(t.k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-widest transition-colors ${tab === t.k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
              data-testid={`stock-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <input
          value={q} onChange={e => setQ(e.target.value)}
          placeholder={tab === "rm" ? "Search raw material…" : "Search SKU…"}
          className="px-3 py-1.5 border border-zinc-300 rounded-lg text-sm w-full sm:w-72 focus:border-orange-500 focus:outline-none"
          data-testid="stock-overview-search"
        />
      </div>

      {/* RM tab — silo tile grid */}
      {tab === "rm" && (
        <div className="p-4 max-h-[520px] overflow-y-auto">
          {rms.length === 0 ? (
            <div className="p-6 text-center text-xs text-zinc-500">No raw materials found</div>
          ) : (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10 gap-3"
              data-testid="rm-silo-grid"
            >
              {rms.map(r => {
                const m = materials.find(x => x.id === r.raw_material_id) || {};
                const low = m.reorder_point && r.total_weight < m.reorder_point;
                const fill = Math.max(6, Math.min(100, ((r.total_weight || 0) / localMax) * 100));
                const active = activeRmId === r.raw_material_id;
                return (
                  <button
                    type="button"
                    key={r.raw_material_id}
                    onClick={() => onPickRm?.(active ? null : { id: r.raw_material_id, name: r.name, code: r.code })}
                    data-testid={`inv-jar-${r.code}`}
                    className={`text-left bg-white border rounded-xl p-3 flex flex-col justify-between shadow-xs hover:shadow-md hover:border-orange-500/80 transition-all duration-300 relative overflow-hidden group ${active ? "border-orange-500 ring-2 ring-orange-300 shadow-md" : low ? "border-red-200 bg-red-50/10" : "border-zinc-200"}`}
                    title={`${r.name} · ${r.total_weight}${r.unit || "kg"} · ${r.bags_count} bags${m.reorder_point ? ` · ROP ${m.reorder_point}` : ""} · click to filter table below`}
                  >
                    <div className="flex items-center gap-2.5">
                      {/* Silo tower */}
                      <div className="relative w-10 h-16 flex-shrink-0 group-hover:scale-105 transition-transform duration-300 flex flex-col justify-end pb-1">
                        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-4 h-1 rounded-t-sm ${low ? "bg-red-500" : "bg-zinc-500"}`} />
                        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-zinc-400 z-10" />
                        <div className={`relative w-full h-11 border border-zinc-300 rounded-t-md bg-gradient-to-b from-zinc-50 to-zinc-100 overflow-hidden ${low ? "ring-1 ring-red-200/50" : ""}`}>
                          <div
                            className={`absolute inset-x-0 bottom-0 transition-all duration-700 ease-out ${low ? "bg-gradient-to-t from-red-600 to-red-400" : "bg-gradient-to-t from-orange-600 to-amber-500"}`}
                            style={{ height: `${fill}%` }}
                          />
                        </div>
                        <div className={`w-full h-3 border-x border-b border-zinc-300 ${low ? "bg-red-100" : "bg-zinc-200"}`} style={{ clipPath: "polygon(0 0, 100% 0, 70% 100%, 30% 100%)" }} />
                        <div className="flex justify-between px-1.5 -mt-0.5 z-10">
                          <div className={`w-0.5 h-1.5 rounded-b-xs ${low ? "bg-red-400" : "bg-zinc-500"}`} />
                          <div className={`w-0.5 h-1.5 rounded-b-xs ${low ? "bg-red-400" : "bg-zinc-500"}`} />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="text-[10px] font-bold font-mono uppercase tracking-wider text-zinc-400 leading-none">{r.code}</div>
                        <div className="font-extrabold text-xs text-zinc-800 truncate tracking-tight">{r.name}</div>
                        <div className={`font-mono text-xs leading-none mt-1 font-extrabold tracking-tight ${low ? "text-red-600" : "text-orange-600"}`}>
                          {r.total_weight}<span className="text-[10px] opacity-80 ml-0.5">{r.unit || "kg"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2.5 flex items-center justify-between border-t border-zinc-100 pt-2 text-[10px] font-mono text-zinc-400">
                      <span>{r.bags_count} {r.bags_count === 1 ? "bag" : "bags"}</span>
                      {active && <span className="text-orange-600 font-bold uppercase">filtering</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* FG tab — drill-down list */}
      {tab === "fg" && (
        <div className="divide-y divide-zinc-100 max-h-[500px] overflow-y-auto">
          {fgs.length === 0 ? (
            <div className="p-6 text-center text-xs text-zinc-500">No SKUs yet — create one in Products</div>
          ) : fgs.map(f => (
            <div key={f.sku_id} data-testid={`sku-tile-${f.sku_id}`}>
              <button
                type="button"
                onClick={() => setExpandedSku(expandedSku === f.sku_id ? null : f.sku_id)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-emerald-50/50 transition-colors"
              >
                <div className="text-left">
                  <div className="font-bold text-sm">
                    {f.name}{f.pack ? <span className="text-zinc-500 font-normal ml-1">· {f.pack}</span> : null}
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500">
                    {f.sku_code || "—"} · {f.batches_count} batch{f.batches_count !== 1 ? "es" : ""} · ₹{f.sale_price}/pack
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-black text-lg">
                    {f.remaining !== undefined ? f.remaining : f.total_produced}
                    <span className="text-xs text-zinc-500 ml-1">packets left</span>
                  </div>
                  {f.remaining_pretty && (
                    <div
                      className="text-[10px] font-mono text-orange-700"
                      data-testid={`fg-remaining-pretty-${f.sku_id}`}
                      title="Grouped by box · sheet · packets"
                    >
                      ≈ {f.remaining_pretty}
                    </div>
                  )}
                  <div className="text-[10px] font-mono text-zinc-500">
                    made {f.total_produced} · sold {f.total_sold || 0}
                  </div>
                  <div className="text-[10px] text-zinc-400">
                    {expandedSku === f.sku_id ? "− hide" : "+ details"}
                  </div>
                </div>
              </button>
              {expandedSku === f.sku_id && (
                <div className="px-4 py-3 bg-zinc-50 border-t border-zinc-100" data-testid={`sku-detail-${f.sku_id}`}>
                  {f.batches.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic">Never produced yet.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                        <th className="py-1">Batch #</th><th>Qty</th><th>COGS/unit</th><th>Date</th>
                      </tr></thead>
                      <tbody>
                        {f.batches.map((b, x) => (
                          <tr key={x} className="border-t border-zinc-200/60">
                            <td className="font-mono py-1.5 font-bold text-orange-700">{b.batch_no}</td>
                            <td className="font-mono">{b.qty} {b.unit}</td>
                            <td className="font-mono">{b.cogs_per_unit !== null && b.cogs_per_unit !== undefined ? `₹${b.cogs_per_unit}` : "—"}</td>
                            <td className="text-zinc-500">{(b.created_at || "").slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


function EditCostModal({ item, onClose, onSaved }) {
  const [cost, setCost] = useState(item.unit_cost || "");
  const [supplier, setSupplier] = useState(item.supplier || "");
  const [supplierBatch, setSupplierBatch] = useState(item.supplier_batch_no || "");
  const [alreadyPaid, setAlreadyPaid] = useState(!!item.already_paid);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/inventory/${item.id}`, {
        unit_cost: parseFloat(cost || 0),
        supplier,
        supplier_batch_no: supplierBatch,
        already_paid: alreadyPaid,
      });
      toast.success("Cost parameter data saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to edit record context"); }
  };

  return (
    <Modal open onClose={onClose} title={`Edit Stock Metadata · ${item.barcode}`}>
      <form onSubmit={submit} className="space-y-4 p-1" data-testid="edit-cost-form">
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm font-mono shadow-2xs">
          <div className="font-bold text-zinc-800">{item.raw_material_name} · {item.weight} {item.unit}</div>
          <div className="text-xs text-zinc-400 mt-0.5">Received log date: {new Date(item.received_at).toLocaleDateString()}</div>
        </div>
        <Field label="Bag Valuation Cost (₹)">
          <Input type="number" step="0.01" value={cost} onChange={e => setCost(e.target.value)} required autoFocus data-testid="edit-cost-input" className="rounded-lg" />
        </Field>
        <Field label="Vendor Supplier Name">
          <Input value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="e.g. Ramesh Traders" data-testid="edit-supplier" className="rounded-lg" />
        </Field>
        <Field label="Supplier Batch Reference ID (optional)">
          <Input value={supplierBatch} onChange={e => setSupplierBatch(e.target.value)} className="rounded-lg" />
        </Field>

        <div className="space-y-1.5 pt-1">
          <label className="flex items-center gap-2.5 text-sm font-mono cursor-pointer text-zinc-700">
            <input type="checkbox" checked={alreadyPaid} onChange={e => setAlreadyPaid(e.target.checked)} data-testid="edit-already-paid" className="w-4 h-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500 accent-orange-600" />
            <span className="font-medium">Flag ledger entry as completely paid</span>
          </label>
          <div className="text-[11px] text-zinc-400 font-mono pl-6 leading-relaxed">
            {alreadyPaid ? "→ Pipeline will post as 'Paid' clearing statement records inside Accounting balances." : "→ Pipeline will post as an outstanding 'Payable' debit file inside pending Accounting."}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100">
          <Btn type="button" variant="secondary" onClick={onClose} className="rounded-lg">Cancel</Btn>
          <Btn type="submit" data-testid="edit-cost-save" className="bg-zinc-950 text-white hover:bg-orange-600 rounded-lg shadow-xs">Commit Financial Details</Btn>
        </div>
      </form>
    </Modal>
  );
}

function AddBagModal({ materials, onClose, onAdded }) {
  const [rmId, setRmId] = useState(materials[0]?.id || "");
  const [weight, setWeight] = useState("50");
  const [unitCost, setUnitCost] = useState("");
  const [supplier, setSupplier] = useState("");
  const [supplierBatch, setSupplierBatch] = useState("");
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/inventory", {
        raw_material_id: rmId,
        weight: parseFloat(weight),
        unit_cost: parseFloat(unitCost || 0),
        supplier,
        supplier_batch_no: supplierBatch,
        already_paid: alreadyPaid,
      });
      toast.success(`Bag assigned barcode: ${r.data.barcode}`);
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const cost = parseFloat(unitCost || 0);

  return (
    <Modal open onClose={onClose} title="Register New Inventory Bag">
      <form onSubmit={submit} className="grid grid-cols-2 gap-4 p-1" data-testid="add-bag-form">
        <div className="col-span-2">
          <Field label="Target Raw Material Asset" full>
            <Select value={rmId} onChange={e => setRmId(e.target.value)} required data-testid="bag-rm" className="rounded-lg">
              {materials.map(m => <option key={m.id} value={m.id}>{m.name} — ({m.code})</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Net Content Weight (kg)">
          <Input type="number" step="0.01" value={weight} onChange={e => setWeight(e.target.value)} required data-testid="bag-weight" className="rounded-lg" />
        </Field>
        <Field label="Total Bag Acquisition Cost (₹)">
          <Input type="number" step="0.01" value={unitCost} onChange={e => setUnitCost(e.target.value)} placeholder="0" data-testid="bag-cost" className="rounded-lg" />
        </Field>
        <div className="col-span-2">
          <Field label="Vendor/Supplier Name Reference" full>
            <Input value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="e.g. Ramesh Traders" data-testid="bag-supplier-name" className="rounded-lg" />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Supplier Batch Serialization #" full>
            <Input value={supplierBatch} onChange={e => setSupplierBatch(e.target.value)} className="rounded-lg" data-testid="bag-supplier" />
          </Field>
        </div>
        <div className="col-span-2 space-y-1.5 bg-zinc-50/50 border border-zinc-100 rounded-lg p-3">
          <label className="flex items-center gap-2.5 text-sm font-mono cursor-pointer text-zinc-700">
            <input type="checkbox" checked={alreadyPaid} onChange={e => setAlreadyPaid(e.target.checked)} data-testid="bag-already-paid" className="w-4 h-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500 accent-orange-600" />
            <span className="font-medium">Remittance fully settled to vendor</span>
          </label>
          {cost > 0 && (
            <div className="text-[11px] text-zinc-400 font-mono pl-6 leading-none">
              {alreadyPaid
                ? `→ System auto-posts ₹${cost.toLocaleString()} straight into Paid ledger histories.`
                : `→ System logs ₹${cost.toLocaleString()} as a deferred Pending Payable.`}
            </div>
          )}
          {cost === 0 && (
            <div className="text-[11px] text-red-500 font-mono pl-6 font-semibold leading-none">
              ⚠️ Valuation balance unassigned. Excluded from dynamic operational asset reports until configured.
            </div>
          )}
        </div>
        <div className="col-span-2 flex justify-end gap-2 pt-2 border-t border-zinc-100">
          <Btn type="button" variant="secondary" onClick={onClose} className="rounded-lg">Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="bag-submit" className="bg-zinc-950 text-white hover:bg-orange-600 rounded-lg shadow-xs">{busy ? "Creating records…" : "Generate Barcode Track ID"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function AddRMModal({ onClose, onAdded }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [unit, setUnit] = useState("kg");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/raw-materials", { name, code: code.toUpperCase(), unit });
      toast.success("Raw material successfully registered inside system catalog");
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Catalog save fault"); }
  };

  return (
    <Modal open onClose={onClose} title="Define New Raw Material Type">
      <form onSubmit={submit} className="grid grid-cols-2 gap-4 p-1" data-testid="add-rm-form">
        <div className="col-span-2">
          <Field label="Material Descriptive Label Name" full><Input value={name} onChange={e => setName(e.target.value)} required data-testid="rm-name" className="rounded-lg" /></Field>
        </div>
        <Field label="System Code (3-4 Chars)"><Input value={code} maxLength={4} onChange={e => setCode(e.target.value.toUpperCase())} required data-testid="rm-code" placeholder="POT" className="rounded-lg font-mono uppercase" /></Field>
        <Field label="Baseline Stock Unit"><Select value={unit} onChange={e => setUnit(e.target.value)} className="rounded-lg"><option>kg</option><option>g</option><option>ltr</option></Select></Field>
        <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-zinc-100">
          <Btn type="button" variant="secondary" onClick={onClose} className="rounded-lg">Cancel</Btn>
          <Btn type="submit" data-testid="rm-submit" className="bg-zinc-950 text-white hover:bg-orange-600 rounded-lg shadow-xs">Add to System Index</Btn>
        </div>
      </form>
    </Modal>
  );
}

function GenerateBagsModal({ pool, onClose, onDone }) {
  const [bags, setBags] = useState([{ weight: pool.remaining_weight }]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [splitCount, setSplitCount] = useState("");

  const total = bags.reduce((s, b) => s + (parseFloat(b.weight) || 0), 0);
  const remaining = pool.remaining_weight - total;
  const balanced = Math.abs(remaining) <= 0.001;

  const setBag = (idx, w) => {
    const c = [...bags];
    c[idx] = { weight: w };
    setBags(c);
  };
  const addBag = () => setBags([...bags, { weight: remaining > 0 ? Number(remaining.toFixed(3)) : 0 }]);
  const rmBag = (idx) => setBags(bags.filter((_, i) => i !== idx));

  const splitEqual = (n) => {
    const count = parseInt(n, 10);
    if (!count || count < 1) { toast.error("Enter a number ≥ 1"); return; }
    if (count > 200) { toast.error("Max 200 bags per split"); return; }
    const w = (pool.remaining_weight / count).toFixed(3);
    setBags(Array.from({ length: count }, () => ({ weight: w })));
  };

  const fillRemaining = () => {
    if (remaining <= 0.001) { toast.info("Weights match perfectly"); return; }
    if (bags.length === 0) {
      setBags([{ weight: pool.remaining_weight.toFixed(3) }]);
    } else {
      const c = [...bags];
      const last = parseFloat(c[c.length - 1].weight || 0);
      c[c.length - 1] = { weight: (last + remaining).toFixed(3) };
      setBags(c);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (total <= 0) { toast.error("Enter valid positive target line values"); return; }
    if (!balanced) {
      toast.error(
        remaining > 0
          ? `Short by ${remaining.toFixed(3)} ${pool.unit}. Adjust fractions to continue.`
          : `Over pool allocation by ${Math.abs(remaining).toFixed(3)} ${pool.unit}.`
      );
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/inventory/${pool.id}/generate-bags`, {
        bags: bags.map(b => parseFloat(b.weight) || 0).filter(w => w > 0),
        notes,
      });
      toast.success(`Successfully segmented ${r.data.created_bag_ids.length} serialized label configurations`);
      try {
        const codes = r.data.created_barcodes;
        const pdfRes = await fetch(`${API}/barcodes/export.pdf`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ barcodes: codes, columns: 3 }),
        });
        if (pdfRes.ok) {
          const blob = await pdfRes.blob();
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `bags-${pool.raw_material_code || "labels"}-${new Date().toISOString().slice(0, 10)}.pdf`;
          a.click();
          setTimeout(() => URL.revokeObjectURL(a.href), 1000);
          toast.success("Printable direct multi-column PDF sheet initialized");
        }
      } catch (pdfErr) {
        // Label PDF is a "nice-to-have" — bag rows are already saved. Log for
        // support but don't fail the whole bag-generation flow with a toast.
        // eslint-disable-next-line no-console
        console.warn("label PDF generation skipped", pdfErr);
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Segmentation batch execution block faulted");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Execute Bag-Out Separation — ${pool.raw_material_name}`} size="lg">
      <form onSubmit={submit} className="space-y-5 p-1" data-testid="gen-bags-form">

        {/* Dynamic Multi Status Tracking Workspace Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs font-mono">
          <div className="border border-zinc-200 p-3 bg-zinc-50 rounded-lg shadow-2xs" data-testid="pool-summary">
            <div className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider">Unassigned Pool Volume</div>
            <div className="font-bold text-lg text-zinc-900 mt-0.5">{pool.remaining_weight} <span className="text-xs font-normal text-zinc-500">{pool.unit}</span></div>
            {pool.linked_po_no && <div className="text-[10px] text-zinc-400 mt-1 truncate">Pipeline context PO #{pool.linked_po_no}</div>}
          </div>
          <div className="border border-zinc-200 p-3 bg-zinc-50 rounded-lg shadow-2xs">
            <div className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider">Configured Bag Total</div>
            <div className={`font-bold text-lg mt-0.5 ${remaining < -0.001 ? "text-red-600" : "text-orange-600"}`} data-testid="bag-total">
              {total.toFixed(3)} <span className="text-xs font-normal text-zinc-500">{pool.unit}</span>
            </div>
          </div>
          <div className={`border p-3 rounded-lg shadow-2xs transition-colors ${balanced ? "border-green-200 bg-green-50/40" : remaining < 0 ? "border-red-200 bg-red-50/40" : "border-amber-200 bg-amber-50/40"}`}>
            <div className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider">Balance Validation</div>
            <div className={`font-bold text-lg mt-0.5 ${balanced ? "text-green-700" : remaining < 0 ? "text-red-600" : "text-amber-600"}`} data-testid="bag-balance">
              {balanced ? "✓ Balanced" : `${remaining.toFixed(3)} ${pool.unit}`}
            </div>
            <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 mt-1">
              {balanced ? "ready for tracking conversion" : remaining > 0 ? "allocation incomplete" : "exceeds maximum limits"}
            </div>
          </div>
        </div>

        {/* Quick presets — one-click bulk split into common bag counts */}
        <div className="border border-orange-200 bg-orange-50/40 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-orange-800">Quick Split</div>
              <div className="text-xs text-zinc-700">
                One-click convert this {pool.remaining_weight}{pool.unit} pool into equal bags
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[2, 5, 10, 20, 25, 50].map(n => {
              const each = (pool.remaining_weight / n).toFixed(2);
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => splitEqual(n)}
                  className="px-2.5 py-1 text-xs font-mono bg-white border border-orange-300 hover:bg-orange-100 rounded transition-colors"
                  data-testid={`split-preset-${n}`}
                  title={`${n} bags of ${each}${pool.unit} each`}
                >
                  {n} × <b>{each}{pool.unit}</b>
                </button>
              );
            })}
          </div>
        </div>

        {/* Dynamic Controls Row Grid */}
        <div className="space-y-2.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Configure Weight Divisions</div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center border border-zinc-300 rounded-lg overflow-hidden bg-white shadow-2xs pl-2.5 h-8" data-testid="split-equal-group">
                <span className="text-[10px] font-bold font-mono uppercase tracking-wider text-zinc-400">Auto Split</span>
                <input
                  type="number" min="1" max="200" step="1"
                  value={splitCount}
                  onChange={e => setSplitCount(e.target.value.replace(/[^0-9]/g, ""))}
                  onBlur={e => {
                    const n = parseInt(e.target.value, 10);
                    if (e.target.value && (isNaN(n) || n < 1 || n > 200)) {
                      toast.error("Value out of scope constraint boundaries");
                      setSplitCount("");
                    }
                  }}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); splitEqual(splitCount); } }}
                  placeholder="Count"
                  className="w-14 px-2 py-1 text-xs font-mono border-l border-zinc-200 focus:outline-none focus:bg-orange-50/40 text-center h-full mx-2"
                  data-testid="split-equal-input"
                />
                <button
                  type="button"
                  onClick={() => { splitEqual(splitCount); setSplitCount(""); }}
                  disabled={!splitCount}
                  data-testid="split-equal-btn"
                  className="px-3 h-full text-[10px] font-bold uppercase tracking-wider bg-zinc-950 text-white hover:bg-orange-600 disabled:opacity-40 transition-colors"
                >
                  Apply
                </button>
              </div>
              <button
                type="button"
                onClick={fillRemaining}
                data-testid="fill-remaining"
                disabled={balanced}
                className="text-xs font-mono uppercase font-bold text-orange-600 hover:text-orange-800 disabled:text-zinc-300 disabled:cursor-not-allowed transition-colors"
              >
                Inject Remainder
              </button>
              <Btn type="button" variant="secondary" onClick={addBag} data-testid="add-bag" className="flex items-center gap-1 border-zinc-200 hover:bg-zinc-50 rounded-lg h-8 text-xs !py-0 px-2.5">
                <Plus size={12} /> Append Row
              </Btn>
            </div>
          </div>

          {/* Core Dynamic Rows List Form Viewports */}
          <div className="max-h-60 overflow-y-auto border border-zinc-200/80 rounded-xl bg-zinc-50/30 divide-y divide-zinc-100 shadow-2xs">
            {bags.map((b, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2.5 hover:bg-white transition-colors">
                <span className="text-xs font-bold font-mono text-zinc-400 w-8 text-center bg-zinc-100 py-0.5 rounded">#{idx + 1}</span>
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={b.weight}
                  onChange={e => setBag(idx, e.target.value)}
                  className="flex-1 px-3 py-1.5 border border-zinc-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/10 focus:border-orange-500 shadow-3xs"
                  placeholder="0.000"
                  data-testid={`bag-w-${idx}`}
                />
                <span className="text-xs font-mono text-zinc-500 font-bold w-6 uppercase">{pool.unit}</span>
                {bags.length > 1 && (
                  <button type="button" onClick={() => rmBag(idx)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all">
                    <X size={15} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <Field label="Operation Ledger Annotations / Batch Context (optional)">
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Fine variance fraction updates recorded from direct cargo sorting logs..." className="rounded-lg" />
        </Field>

        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100">
          <Btn type="button" variant="secondary" onClick={onClose} className="rounded-lg">Cancel</Btn>
          <Btn type="submit" disabled={busy || !balanced} data-testid="gen-bags-submit" className="bg-zinc-950 text-white hover:bg-orange-600 disabled:bg-zinc-200 disabled:text-zinc-400 disabled:opacity-100 rounded-lg shadow-xs transition-colors">
            {busy
              ? "Running Computations…"
              : !balanced
                ? (remaining > 0 ? `Incomplete: Short by ${remaining.toFixed(3)} ${pool.unit}` : `Capacity Exceeded: Over by ${Math.abs(remaining).toFixed(3)} ${pool.unit}`)
                : `Commit and Construct ${bags.filter(b => (parseFloat(b.weight) || 0) > 0).length} Serial Barcodes`}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

function MarkUsedModal({ item, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/inventory/${item.id}/mark-used`, { reason: reason.trim() });
      toast.success(`Stock item data profile ${item.barcode} closed out`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Execution fault encountered");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Manual Stock Depletion Adjustments" size="sm">
      <form onSubmit={submit} className="space-y-4 p-1" data-testid="mark-used-form">
        <div className="bg-amber-50/60 border-l-4 border-amber-500 p-3.5 rounded-r-lg text-xs leading-relaxed text-zinc-700 shadow-3xs">
          <div className="font-extrabold uppercase tracking-wider text-[10px] text-amber-800 mb-1">Important Ledger Notice</div>
          <div>
            This action instantly marks item <span className="font-mono font-bold text-zinc-900">{item.raw_material_name}</span>
            {" "}(<span className="font-mono text-orange-700 font-semibold">{item.barcode}</span>) with capacity balance
            {" "}<span className="font-mono font-bold text-zinc-900">{item.weight}{item.unit}</span> as fully consumed.
            No subsequent batch yields or structural outputs will be tracked — this represents an <strong>accounting-only modification</strong> sequence (e.g. spoilage write-offs, R&D sampling loss logs).
          </div>
        </div>
        <Field label="Documented Depletion Reason / Audit Line Context">
          <Input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Structural cargo spoilage write-off, internal lab R&D sample..."
            data-testid="mark-used-reason"
            className="rounded-lg"
            required
            autoFocus
          />
        </Field>
        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100">
          <Btn type="button" variant="secondary" onClick={onClose} className="rounded-lg">Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="mark-used-submit" className="bg-zinc-950 text-white hover:bg-orange-600 rounded-lg shadow-xs transition-colors">
            {busy ? "Updating Ledger Data…" : "Authorize Stock Write-off"}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- Bag History Drawer
// Shows every partial-consumption event for a single inventory bag: what was
// consumed, what was left behind, the operator's note, and when it happened.
function BagHistoryModal({ bag, onClose }) {
  const history = Array.isArray(bag.partial_history) ? bag.partial_history : [];
  const ageDays = bag.partial_since
    ? Math.floor((Date.now() - new Date(bag.partial_since).getTime()) / 86400000)
    : null;
  return (
    <Modal open onClose={onClose} title={`Bag history · ${bag.barcode || bag.id}`} size="md">
      <div className="space-y-4" data-testid="bag-history-modal">
        {/* Bag summary */}
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div className="border border-zinc-200 rounded p-2 bg-zinc-50">
            <div className="kr-label">Raw material</div>
            <div className="font-bold text-sm text-zinc-900 truncate">{bag.raw_material_name || "—"}</div>
          </div>
          <div className="border border-zinc-200 rounded p-2 bg-zinc-50">
            <div className="kr-label">Remaining</div>
            <div className="font-mono font-bold text-sm text-zinc-900">{bag.remaining_weight}{bag.unit || "kg"} / {bag.weight}{bag.unit || "kg"}</div>
          </div>
          <div className="border border-zinc-200 rounded p-2 bg-zinc-50">
            <div className="kr-label">Opened since</div>
            <div className="font-mono font-bold text-sm text-zinc-900">
              {bag.partial_since ? `${ageDays}d ago` : "—"}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div>
          <div className="kr-label mb-2">Partial consumption timeline</div>
          {history.length === 0 ? (
            <p className="text-xs text-zinc-500 italic">
              No partial consumption events recorded. Either this bag was never opened, or every consumption emptied it fully.
            </p>
          ) : (
            <div className="border border-zinc-200 rounded divide-y divide-zinc-100">
              {history.map((h, idx) => {
                const when = h.at ? new Date(h.at) : null;
                return (
                  <div
                    key={idx}
                    className="p-2.5 text-xs flex flex-col gap-1"
                    data-testid={`bag-history-row-${idx}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono font-bold text-orange-700">−{h.consumed_kg}{bag.unit || "kg"} used</span>
                      <span className="text-zinc-400">·</span>
                      <span className="font-mono text-green-700">{h.remaining_kg}{bag.unit || "kg"} left in bag</span>
                      <span className="ml-auto text-[10px] font-mono text-zinc-500">
                        {when ? when.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                      </span>
                    </div>
                    {h.note && (
                      <div className="text-[11px] text-zinc-700 italic bg-zinc-50 border-l-2 border-orange-300 pl-2 py-0.5">
                        &ldquo;{h.note}&rdquo;
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t border-zinc-100">
          <Btn type="button" variant="secondary" onClick={onClose}>Close</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- Bag Usage Modal
// For bagged-out/consumed bags: shows every batch that pulled weight from this
// specific bag — batch number, product, weight consumed, cost, when + who.
function BagUsageModal({ bag, onClose }) {
  const [state, setState] = React.useState({ loading: true, data: null, err: "" });

  React.useEffect(() => {
    let mounted = true;
    api.get(`/inventory/${bag.id}/usage`)
      .then(r => { if (mounted) setState({ loading: false, data: r.data, err: "" }); })
      .catch(e => { if (mounted) setState({ loading: false, data: null, err: e?.response?.data?.detail || "Failed to load" }); });
    return () => { mounted = false; };
  }, [bag.id]);

  const events = state.data?.events || [];
  const totalConsumed = events.reduce((s, e) => s + (Number(e.weight_consumed) || 0), 0);

  return (
    <Modal open onClose={onClose} title={`Bag usage · ${bag.barcode || bag.id}`} size="md">
      <div className="space-y-4" data-testid="bag-usage-modal">
        {/* Printable region — wrapped so operators can hand a physical audit
            sheet back to whoever needs it (letterhead-styled). */}
        <div data-testid="bag-usage-print" className="space-y-4 bg-white print:m-0">
          <div className="hidden print:block border-b border-zinc-300 pb-2 mb-3">
            <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Bag Usage Audit</div>
            <h3 className="text-xl font-black tracking-tight">
              {bag.barcode || bag.id}
            </h3>
            <div className="text-xs font-mono text-zinc-600">
              {bag.raw_material_name} · Printed {new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
            </div>
          </div>
          {/* Summary tiles */}
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="border border-zinc-200 rounded p-2 bg-zinc-50 print:bg-white">
              <div className="kr-label">Raw material</div>
              <div className="font-bold text-sm text-zinc-900 truncate">{bag.raw_material_name || "—"}</div>
            </div>
            <div className="border border-zinc-200 rounded p-2 bg-zinc-50 print:bg-white">
              <div className="kr-label">Total consumed</div>
              <div className="font-mono font-bold text-sm text-zinc-900">
                {totalConsumed.toFixed(3)}{bag.unit || "kg"} <span className="text-zinc-500">/ {bag.weight}{bag.unit || "kg"}</span>
              </div>
            </div>
            <div className="border border-zinc-200 rounded p-2 bg-zinc-50 print:bg-white">
              <div className="kr-label">Batches used in</div>
              <div className="font-mono font-bold text-sm text-zinc-900">{events.length}</div>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <div className="kr-label mb-2">Consumption timeline</div>
            {state.loading ? (
              <div className="text-xs text-zinc-500 italic">Loading…</div>
            ) : state.err ? (
              <div className="text-xs text-red-600">{state.err}</div>
            ) : events.length === 0 ? (
              <div className="text-xs text-zinc-500 italic">This bag has not been consumed by any batch yet.</div>
            ) : (
              <div className="border border-zinc-200 rounded divide-y divide-zinc-100 max-h-80 overflow-y-auto print:max-h-none print:overflow-visible">
                {events.map((e, idx) => {
                  const when = e.at ? new Date(e.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";
                  return (
                    <div key={idx} className="p-2.5 text-xs" data-testid={`bag-usage-row-${idx}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to="/batches"
                          onClick={onClose}
                          className="font-mono font-bold text-orange-700 hover:underline print:text-zinc-900 print:no-underline"
                        >
                          {e.batch_no || e.batch_id.slice(0, 8)}
                        </Link>
                        <span className="text-zinc-800">{e.product}</span>
                        <span className="ml-auto font-mono text-orange-700 print:text-zinc-900">−{e.weight_consumed}{e.unit || "kg"}</span>
                        {e.cost > 0 && <span className="font-mono text-zinc-600">₹{e.cost}</span>}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                        {e.by || "—"} · {when}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* Signature block for physical audit — only on print */}
          <div className="hidden print:grid grid-cols-2 gap-6 mt-8 pt-4">
            <div>
              <div className="border-t border-zinc-400 pt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-600">Verified by</div>
            </div>
            <div>
              <div className="border-t border-zinc-400 pt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-600">Date / Signature</div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
          <Btn
            type="button"
            variant="secondary"
            onClick={() => printDom('[data-testid="bag-usage-print"]', `Bag Usage ${bag.barcode || bag.id}`)}
            data-testid="bag-usage-print-btn"
          >
            Print
          </Btn>
          <Btn type="button" variant="secondary" onClick={onClose}>Close</Btn>
        </div>
      </div>
    </Modal>
  );
}


// ---------------------------------------------------------------- Bulk Bag Audit
// Pick a start + end date, fetch every bag that was fully consumed in that
// window, and print every Bag Usage sheet in one shot. Saves 40+ clicks at
// month-end.
function BulkBagAuditModal({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
  const [start, setStart] = React.useState(monthAgo);
  const [end, setEnd] = React.useState(today);
  const [state, setState] = React.useState({ loading: false, sheets: [], err: "" });

  const generate = async () => {
    setState({ loading: true, sheets: [], err: "" });
    try {
      const r = await api.get("/inventory/bulk-usage", { params: { start, end } });
      setState({ loading: false, sheets: r.data.sheets || [], err: "" });
    } catch (e) {
      setState({ loading: false, sheets: [], err: e?.response?.data?.detail || "Failed to load" });
    }
  };

  const doPrint = () => printDom('[data-testid="bulk-audit-print"]', `Bag Audit ${start} to ${end}`);

  return (
    <Modal open onClose={onClose} title="Bulk Bag Audit" size="lg">
      <div className="space-y-4" data-testid="bulk-audit-modal">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs">
            <span className="kr-label">From</span>
            <Input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="bulk-audit-start" />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="kr-label">To</span>
            <Input type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid="bulk-audit-end" />
          </label>
          <Btn type="button" onClick={generate} disabled={state.loading} data-testid="bulk-audit-generate">
            {state.loading ? "Loading…" : "Generate Sheets"}
          </Btn>
          {state.sheets.length > 0 && (
            <Btn type="button" variant="secondary" onClick={doPrint} data-testid="bulk-audit-print-btn">
              Print all ({state.sheets.length})
            </Btn>
          )}
        </div>

        {state.err && <div className="text-xs text-red-600">{state.err}</div>}

        {state.sheets.length === 0 && !state.loading && !state.err && (
          <div className="text-xs text-zinc-500 italic">Pick a range and click Generate.</div>
        )}

        <div data-testid="bulk-audit-print" className="space-y-6 max-h-[500px] overflow-y-auto print:max-h-none print:overflow-visible">
          {state.sheets.map((sheet, i) => {
            const bag = sheet.bag;
            const events = sheet.events || [];
            const total = events.reduce((s, e) => s + (Number(e.weight_consumed) || 0), 0);
            return (
              <div
                key={bag.id}
                className={`border border-zinc-200 rounded p-3 print:border-t-2 print:border-b-0 print:border-x-0 ${i > 0 ? "print:break-before-page" : ""}`}
                data-testid={`bulk-sheet-${bag.barcode || bag.id}`}
              >
                <div className="border-b border-zinc-200 pb-2 mb-2">
                  <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Bag Usage Audit</div>
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-base font-black tracking-tight">{bag.barcode || bag.id}</h3>
                    <div className="text-xs font-mono text-zinc-600">
                      {bag.raw_material_name} · {total.toFixed(3)}{bag.unit} used / {bag.weight}{bag.unit}
                    </div>
                  </div>
                </div>
                {events.length === 0 ? (
                  <div className="text-xs text-zinc-500 italic">No batches consumed from this bag.</div>
                ) : (
                  <div className="border border-zinc-100 divide-y divide-zinc-100 text-xs">
                    {events.map((e, x) => {
                      const when = e.at ? new Date(e.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";
                      return (
                        <div key={x} className="p-1.5 flex flex-wrap items-center gap-2">
                          <span className="font-mono font-bold text-orange-700 print:text-zinc-900">{e.batch_no || e.batch_id.slice(0, 8)}</span>
                          <span>{e.product}</span>
                          <span className="ml-auto font-mono">−{e.weight_consumed}{bag.unit}</span>
                          <span className="text-zinc-500">{e.by} · {when}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="hidden print:grid grid-cols-2 gap-6 mt-4 pt-2">
                  <div>
                    <div className="border-t border-zinc-400 pt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-600">Verified by</div>
                  </div>
                  <div>
                    <div className="border-t border-zinc-400 pt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-600">Date / Signature</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end pt-2 border-t border-zinc-100">
          <Btn type="button" variant="secondary" onClick={onClose}>Close</Btn>
        </div>
      </div>
    </Modal>
  );
}

